import { A9Component, Controller, type IEventEmitter } from '../../system';
import { type IDialogManager } from '../../popup';
import { binaryStringToFile, mergeDirectiveOptions } from './index';

export interface IImageControl extends IEventEmitter {
    readonly UPDATED: string;
    readonly DELETED: string;

    getImageFile(): IUploadedImage;
    isChanged(): boolean;
    deleteImage(): void;
}

export interface IUploadedImage {
    content: Blob; // blob source string
    fileUrlString?: string; // blob string
    name: string; // filename
    type: string; // file type
    cropperData: any; //
}

export type ImageControlTState = {
    file?: IUploadedImage['content'];
    fileName?: IUploadedImage['name'];
    fileType?: IUploadedImage['type'];
    cropperData?: IUploadedImage['cropperData'];
    fileChanged?: boolean;
};

export interface IScope extends angular.IScope {
    getDecodedName: () => string;
    state: ImageControlTState;
    onReady(params: { api: IImageControl }): void;
    image: IUploadedImage;
    maxWeight: any;
    imageName: any; // TODO
    imageSize: any; // TODO
    shape?: string;
    actions?: {
        browse?: boolean;
        snapshot?: boolean;
    };
    controls?: boolean;
    imageInfo: {
        isImage: boolean;
        cssUrl: string;
        srcUrl: string;
    };
    favicon: boolean;
    displayActions: boolean;
    blobUrl: string;
    imageUrl: string;
    pStyle: {
        backgroundPosition?: string;
        backgroundSize?: string;
    };
    toggleActionMenu(): void;
    closeActionMenu(): void;
    takePicture(): void;
    uploadImage(): void;
    deleteImage(): void;
    editImage(): void;
}

const IMAGE_SIZE = 80;
const UPDATED = 'updated';
const DELETED = 'deleted';

@A9Component(
    mergeDirectiveOptions({
        selector: 'vi-image-control',
        scope: {
            controls: '<',
            imageUrl: '<?',
            favicon: '=?'
        }
    })
)
export class ImageControl extends Controller implements IImageControl {
    public fileChanged = false;
    private $timeout: angular.ITimeoutService;
    private photoService;
    private fsKeyboard;
    private fsListeners = [];
    protected dialogManager: IDialogManager;

    constructor(
        protected $scope: IScope,
        protected $element: angular.IAugmentedJQuery,
        protected $injector: angular.auto.IInjectorService
    ) {
        super($scope);

        this.$timeout = $injector.get('$timeout');
        this.fsKeyboard = $injector.get('fsKeyboard');
        this.dialogManager = $injector.get('dialogManager');
        this.photoService = $injector.get('photoService');

        this.initScope();
        this.initState().then(() => {
            $scope.onReady({ api: this });
        });
    }

    public get UPDATED(): string {
        return UPDATED;
    }

    public get DELETED(): string {
        return DELETED;
    }

    public getImageFile(): IUploadedImage {
        const $scope = this.$scope;

        if (!$scope.state.file) {
            return null;
        }
        return {
            content: $scope.state.file,
            name: $scope.state.fileName,
            type: $scope.state.fileType,
            cropperData: $scope.state.cropperData || null
        };
    }

    public isChanged(): boolean {
        return this.$scope.state.fileChanged;
    }

    protected crop(file): Promise<any> {
        return new Promise(resolve => {
            const scope: any = this.$scope.$new(true);
            let loading = false,
                cropApi = null;

            scope.blobUrl = this.$scope.blobUrl || this.$scope.imageInfo.srcUrl;
            scope.cropperData = (this.$scope.state.cropperData && this.$scope.state.cropperData.cropperData) || null;
            scope.shape = this.$scope.shape;

            scope.connectCropApi = api => {
                cropApi = api;

                dialog.ready.then(() => {
                    cropApi && cropApi.stopLoading();
                });
            };

            const dialog = this.dialogManager.dialog({
                scope: scope,
                title: 'ctrl.image.crop.title',
                scrollbar: true,
                template: `<div style="width: 500px; height: 370px;">
                                <image-crop-form state="state.crop"
                                                 on-ready="connectCropApi(api)"
                                                 image-url="blobUrl"
                                                 cropper-data="cropperData" ,
                                                 shape="shape"
                                                 async-component>
                                </image-crop-form>
                            </div>`,
                buttons: [
                    {
                        title: 'ctrl.image.crop.takeCameraSnapshot',
                        classes: 'secondary',
                        ngStyle: {
                            float: 'left',
                            marginLeft: 0,
                            display: this.$scope.actions.snapshot ? 'block' : 'none'
                        },
                        action: popupScope => {
                            this.takePicture();
                            popupScope.$dismiss();
                        },
                        disabled: () => !this.$scope.actions.snapshot || !cropApi || loading
                    },
                    {
                        title: 'common.actions.cancel',
                        classes: 'cancel',
                        action: popupScope => popupScope.$dismiss()
                    },
                    {
                        title: 'ctrl.image.crop.cropAndSave',
                        action: () => {
                            loading = true;

                            this.saveFileState(file, cropApi.getCropperData(this.$scope.imageSize || IMAGE_SIZE))
                                .then(() => {
                                    dialog.close();
                                    this.$scope.favicon = false;
                                })
                                .finally(() => {
                                    loading = false;
                                });
                        },
                        disabled: () => !cropApi,
                        busy: () => loading
                    }
                ]
            });

            dialog.result
                .catch(() => {})
                .then(() => {
                    resolve('');
                    scope.$destroy();
                    // this.$scope.state.activeDialog = ''; // TODO where used? why this in common component?
                });
        });
    }

    protected uploadImage(): void {
        this.closeActionMenu();
        this.upload(null, true);
    }

    protected upload(file = null, needCrop: boolean = true): Promise<any> {
        const fileElement: any = this.$element.find('.image-control__file').first();

        fileElement.val('');

        const imageFile = new Promise<File>(resolve => {
            if (!file) {
                // if get file from upload input field
                fileElement[0].onchange = () => {
                    resolve(fileElement[0].files[0]);
                };
                setTimeout(() => {
                    fileElement.click();
                });
            } else {
                // if get file from param, from camera photo for example
                resolve(file);
            }
        });

        return imageFile
            .then((file: File) => {
                return new Promise<File>(resolve => {
                    return resolve(file);
                });
            })
            .then((file: File) => {
                return new Promise<File>((resolve, reject) => {
                    if (this.$scope.maxWeight && file.size / 1024 > this.$scope.maxWeight) {
                        reject('weight');
                    } else {
                        const image = new Image();
                        image.src = window.URL.createObjectURL(file);
                        image.onload = () => {
                            this.$scope.blobUrl = image.src;
                            resolve(file);
                        };
                        image.onerror = () => {
                            reject();
                        };
                    }
                });
            })
            .then((file: File) => {
                if (needCrop) {
                    return this.crop(file);
                } else {
                    return this.saveFileState(file, null);
                }
            })
            .catch(err => {
                if (err === 'weight') {
                    this.dialogManager.error(
                        `<span i18n='ctrl.image.crop.image_size_error' i18n-variables="{weight: ${this.$scope.maxWeight}}"></span>`
                    );
                }
                if (err === 'abort') {
                    // no display error on user abort
                    return;
                }
            });
    }

    protected saveFileState(file: File, cropperData): Promise<any> {
        const state = this.$scope.state;

        state.fileChanged = true;

        return new Promise<void>(resolve => {
            state.cropperData = cropperData;
            if (file) {
                const reader = new FileReader();

                reader.onload = (event: Event) => {
                    state.fileName = file.name || 'camerashot.png';
                    state.fileType = file.type;
                    state.file = event['target']['result']; // TODO: type is not Event
                    this.updateImageView();
                    resolve();
                };

                reader.readAsBinaryString(file);
            } else {
                this.updateImageView();
                resolve();
            }
        });
    }

    protected closeActionMenu(): void {
        this.$scope.displayActions = false;
        this.clearFsListeners();
    }

    private async initState(): Promise<void> {
        const $scope = this.$scope;
        let resolvePromise, rejectPromise;
        const promise = new Promise<void>(function (resolve, reject) {
            rejectPromise = reject;
            resolvePromise = resolve;
        });

        if (!$scope.state) {
            $scope.state = {};

            if ($scope.image) {
                if ($scope.image.content && $scope.image.content instanceof Blob) {
                    const reader = new FileReader();

                    reader.onload = event => {
                        const image = {
                            content: event['target']['result'],
                            name: $scope.image.name,
                            type: $scope.image.type,
                            cropperData: $scope.image.cropperData
                        };
                        this.loadFromImage(image);
                        resolvePromise();
                    };

                    reader.readAsBinaryString($scope.image.content);
                    return promise;
                }
                this.loadFromImage($scope.image);
            }
        } else if ($scope.state.file) {
            this.updateImageView();
        }

        resolvePromise();
        return promise;
    }

    protected initScope() {
        const $scope = this.$scope;

        $scope.shape = $scope.shape || 'circle';
        $scope.actions = $scope.actions || {
            browse: true,
            snapshot: true
        };

        $scope.getDecodedName = () => decodeURIComponent($scope.state.fileName);

        this.$element.addClass('image-control');
        $scope.imageInfo = {
            isImage: false,
            cssUrl: null,
            srcUrl: null
        };

        $scope.toggleActionMenu = this.toggleActionMenu.bind(this);
        $scope.closeActionMenu = this.closeActionMenu.bind(this);
        $scope.takePicture = this.takePicture.bind(this);
        $scope.uploadImage = this.uploadImage.bind(this);
        $scope.deleteImage = this.deleteImage.bind(this);
        $scope.editImage = this.editImage.bind(this);
    }

    private toggleActionMenu(): void {
        const $scope = this.$scope;

        $scope.displayActions = !$scope.displayActions;

        if ($scope.displayActions) {
            this.fsListeners.push(
                this.fsKeyboard.on(
                    'esc',
                    (parametr, eventCtrl) => {
                        eventCtrl.stop();
                        this.closeActionMenu();
                    },
                    { addBefore: true }
                )
            );
        } else {
            this.clearFsListeners();
        }
    }

    private takePicture(): void {
        this.closeActionMenu();
        this.photoService
            .takePhoto(this.$scope)
            .then(file => {
                if (file) {
                    this.upload(file.blob, true);
                } else {
                    this.empty();
                }
            })
            .catch(err => {
                err && this.dialogManager.error(err, this.$scope);
            });
    }

    private editImage(): void {
        this.closeActionMenu();
        this.crop(null);
    }

    public deleteImage(): void {
        const $scope = this.$scope;

        this.closeActionMenu();

        $scope.state.file = null;
        $scope.state.fileName = '';
        $scope.state.fileType = '';
        $scope.state.cropperData = null;

        $scope.imageInfo.isImage = false;
        $scope.state.fileChanged = true;

        this.$timeout(() => {
            this.emit(this.DELETED);
        });
    }

    private loadFromImage(image): void {
        const state = this.$scope.state;

        state.file = image.content || null;
        state.fileName = image.name || '';
        state.fileType = image.type || '';
        state.cropperData = image.cropperData || null;
        this.updateImageView();
    }

    protected updateImageView(): void {
        const imageInfo = this.$scope.imageInfo;
        const { file, fileName, fileType } = this.$scope.state;

        if (file) {
            const fileObj = binaryStringToFile(file, fileName, fileType);
            const url = window.URL.createObjectURL(fileObj);

            imageInfo.isImage = true;
            imageInfo.cssUrl = url ? `url("${url}")` : '';
            imageInfo.srcUrl = url;
        } else {
            imageInfo.isImage = false;
            imageInfo.cssUrl = '';
            imageInfo.srcUrl = '';
        }
        this.updateImageCropping();
        this.$scope.$applyAsync();
        this.emit(this.UPDATED);
    }

    protected updateImageCropping(): void {
        const $scope = this.$scope;
        const cropperData = $scope.state.cropperData;

        $scope.pStyle = {};

        if (cropperData && cropperData.styles) {
            $scope.pStyle.backgroundPosition = cropperData.styles.backgroundPosition;
            $scope.pStyle.backgroundSize = cropperData.styles.backgroundSize;
        } else {
            $scope.pStyle.backgroundPosition = '50% 50%';
            $scope.pStyle.backgroundSize = 'cover';
        }
    }

    private empty(): void {
        const canvas = document.createElement('canvas');

        canvas.width = 1;
        canvas.height = 1;
        canvas.toBlob(blob => {
            this.upload(blob, false);
        });
    }

    private getPictureNode(): HTMLElement {
        return this.$element[0].querySelector('.image-picture');
    }

    private clearFsListeners(): void {
        this.fsListeners.forEach(remove => remove());
        this.fsListeners = [];
    }
}
